import { TCountry, TEntity, TLanguage, TUserType } from 'contexts/app-context/types'
import { IS_APP, IS_DEV_ENV } from 'common/constants'

import { TrackData } from '@interco/track'
import { interWbSession } from '@interco/inter-webview-bridge'

type NewRelicData = {
  status: 'SUCCESS' | 'ERROR' | 'REQUESTING'
  transactionType: 'BRIDGE_API_CALL' | 'HTTP_API_CALL' | 'COMPONENT_RENDER'
  transactionSubType: string
  detail: string
}

type NewRelicRequest = NewRelicData & {
  socialId: string
  accountNumber: string
  country: TCountry
  userType: TUserType
  entity: TEntity
  language: TLanguage
}

type TraceDataProps = {
  data: NewRelicData
  context?: Partial<{
    country: TCountry
    userType: TUserType
    entity: TEntity
    language: TLanguage
  }>
}

type TTraceUserInfo = {
  accountNumber: string
  socialId: string
}

const TRACE_USER_INFO_TIMEOUT_MS = 1500

let cachedTraceUserInfo: TTraceUserInfo | null = null
let pendingTraceUserInfoRequest: Promise<TTraceUserInfo | null> | null = null

const logTraceBridgeError = (error: unknown) => {
  if (IS_DEV_ENV) {
    global.console.debug('Failed to get user info for trace', error)
  }
}

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const normalizeTraceUserInfo = (value: unknown): TTraceUserInfo | null => {
  if (!isObjectRecord(value)) return null

  const accountNumber = value.account
  const socialId = value.cpf

  if (typeof accountNumber !== 'string' || typeof socialId !== 'string') {
    return null
  }

  return { accountNumber, socialId }
}

const getUserInfoWithTimeout = () =>
  new Promise<TTraceUserInfo | null>((resolve) => {
    const timeoutId = window.setTimeout(() => resolve(null), TRACE_USER_INFO_TIMEOUT_MS)

    interWbSession
      .getUserInfo()
      .then((userInfo) => resolve(normalizeTraceUserInfo(userInfo)))
      .catch((error: unknown) => {
        logTraceBridgeError(error)
        resolve(null)
      })
      .finally(() => {
        window.clearTimeout(timeoutId)
      })
  })

const getTraceUserInfo = async () => {
  if (cachedTraceUserInfo) return cachedTraceUserInfo
  if (pendingTraceUserInfoRequest) return pendingTraceUserInfoRequest

  pendingTraceUserInfoRequest = getUserInfoWithTimeout()
    .then((userInfo) => {
      cachedTraceUserInfo = userInfo
      return userInfo
    })
    .finally(() => {
      pendingTraceUserInfoRequest = null
    })

  return pendingTraceUserInfoRequest
}

const traceData = ({ data, context }: TraceDataProps) => {
  if (IS_APP && !IS_DEV_ENV) {
    const request: Partial<NewRelicRequest> = {
      ...data,
    }

    request.country = context?.country
    request.userType = context?.userType
    request.entity = context?.entity
    request.language = context?.language

    getTraceUserInfo()
      .then((userData) => {
        if (userData) {
          request.socialId = userData.socialId
          request.accountNumber = userData.accountNumber
        }

        TrackData.log(request)
      })
      .catch((error: unknown) => {
        logTraceBridgeError(error)
        TrackData.log(request)
      })
  }
}

export type { NewRelicData, TraceDataProps }

export { traceData }
